// views/Dashboard/PainelExecutivo/components/SeletorDepartamentos.tsx
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { DepartamentoComercial } from '../types';
import { OPCOES_DEPARTAMENTO } from '../types';

type DepartamentoReal = Exclude<DepartamentoComercial, 'todos'>;

interface Props {
  selecionados: DepartamentoReal[];
  onChange: (deps: DepartamentoReal[]) => void;
  /** kWh por canal, para cada opção mostrar o próprio peso. Opcional: sem ele,
   *  a lista aparece só com os nomes. */
  volumeKwh?: ReadonlyMap<DepartamentoComercial, number>;
  /** O que o número ao lado de cada canal significa — vai no topo da lista e no
   *  tooltip. Sem ele, um kWh solto ali seria lido como qualquer coisa. */
  legendaVolume?: string;
}

// 'todos' é um rótulo de UI, não um canal — a lista real são os canais abaixo.
const OPCOES_REAIS = OPCOES_DEPARTAMENTO.filter(
  (o): o is { value: DepartamentoReal; label: string } => o.value !== 'todos'
);

// Sempre em kWh, com separador de milhar: é a unidade dos cards do funil, e
// misturar MWh aqui obrigava a converter de cabeça para comparar.
const fmtKwh = (v: number) => `${Math.round(v).toLocaleString('pt-BR')} kWh`;

// Dropdown com checkboxes — deixa escolher qualquer combinação de canais.
const SeletorDepartamentos: React.FC<Props> = ({ selecionados, onChange, volumeKwh, legendaVolume }) => {
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const aoClicarFora = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [aberto]);

  const toggle = (valor: DepartamentoReal) => {
    if (selecionados.includes(valor)) onChange(selecionados.filter((v) => v !== valor));
    else onChange([...selecionados, valor]);
  };

  // "Todos" marca tudo ou desmarca tudo de uma vez. Desmarcar tudo é o mesmo
  // que não filtrar (ver `filtrarPorDepartamento`), então a tela nunca fica
  // vazia — mas o rótulo diz "Todos os canais" para isso não parecer defeito.
  const todosMarcados = selecionados.length === OPCOES_REAIS.length;
  const alternarTodos = () => onChange(todosMarcados ? [] : OPCOES_REAIS.map((o) => o.value));

  const semFiltro = selecionados.length === 0 || todosMarcados;
  const rotulo = semFiltro
    ? 'Todos os canais'
    : selecionados.length <= 2
      ? OPCOES_REAIS.filter((o) => selecionados.includes(o.value)).map((o) => o.label).join(', ')
      : `${selecionados.length} canais`;

  const kwhDe = (valor: DepartamentoReal) => volumeKwh?.get(valor) ?? 0;
  const kwhTotal = OPCOES_REAIS.reduce((s, o) => s + kwhDe(o.value), 0);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        {rotulo}
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${aberto ? 'rotate-180' : ''}`} />
      </button>

      {aberto && (
        <div className="absolute z-20 mt-2 right-0 w-80 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl shadow-xl p-2">
          {volumeKwh && legendaVolume && (
            <p className="px-3 pt-1 pb-2 text-right text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {legendaVolume}
            </p>
          )}
          <label className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer text-sm font-semibold text-gray-800 dark:text-gray-100">
            <input
              type="checkbox"
              checked={todosMarcados}
              // Nem todos, nem nenhum: o traço no meio mostra seleção parcial.
              ref={(el) => { if (el) el.indeterminate = !todosMarcados && selecionados.length > 0; }}
              onChange={alternarTodos}
              className="rounded border-gray-300 text-primary focus:ring-primary/40"
            />
            <span className="flex-1">Todos</span>
            {volumeKwh && (
              <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 tabular-nums" title={legendaVolume ? `Total — ${legendaVolume}` : undefined}>
                {fmtKwh(kwhTotal)} assinados
              </span>
            )}
          </label>

          <div className="my-1 border-t border-gray-100 dark:border-gray-700" />

          {OPCOES_REAIS.map((o) => (
            <label
              key={o.value}
              className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer text-sm text-gray-700 dark:text-gray-200"
            >
              <input
                type="checkbox"
                checked={selecionados.includes(o.value)}
                onChange={() => toggle(o.value)}
                className="rounded border-gray-300 text-primary focus:ring-primary/40"
              />
              <span className="flex-1 truncate">{o.label}</span>
              {volumeKwh && (
                <span
                  className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 tabular-nums"
                  title={legendaVolume ? `${o.label} — ${legendaVolume}` : undefined}
                >
                  {fmtKwh(kwhDe(o.value))} assinados
                </span>
              )}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default SeletorDepartamentos;
